﻿import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { ChevronDown, ChevronsLeft, ChevronsRight, MoreVertical } from 'lucide-react';
import { useAuth, AllowedMenu } from '../contexts/AuthContext';
import { getIcon, getCategoryColorFromPath } from '../utils/iconMapper';
import { useLayout } from '../contexts/LayoutContext';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import api from '../services/api';
import { formatName, getInitials } from '@/lib/utils';
import { useModuleNavScope } from '../hooks/useModuleNavScope';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  /** If true, render even when layout context says layout exists (used by AppLayout) */
  forceRender?: boolean;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

// Group menus by their section (first-level parent or standalone)
interface MenuSection {
  id: string;
  label: string;
  /** Tenant's own display name, see getMenuNavDisplayLabel. */
  customLabel?: string | null;
  path: string;
  icon: string | null;
  isExpandable: boolean;
  children: AllowedMenu[];
}

/** Under Settings, Org Setup (incl. legacy /team path) before Profiles. */
function compareAllowedMenusBySortOrder(
  a: Pick<AllowedMenu, 'path' | 'sortOrder' | 'label'>,
  b: Pick<AllowedMenu, 'path' | 'sortOrder' | 'label'>,
): number {
  const settingsLeafRank = (path: string): number | null => {
    const p = path.toLowerCase();
    if (!p.includes('/settings')) return null;
    if (p.includes('org_setup') || p.includes('org-setup') || /(\/|^)team(\/|$)/.test(p)) return 0;
    if (p.includes('company_profile') || p.includes('company-profile')) return 1;
    return null;
  };
  const ra = settingsLeafRank(a.path);
  const rb = settingsLeafRank(b.path);
  if (ra !== null && rb !== null && ra !== rb) return ra - rb;

  const orderA = a.sortOrder ?? 0;
  const orderB = b.sortOrder ?? 0;
  if (orderA !== orderB) return orderA - orderB;
  return a.label.localeCompare(b.label);
}

/**
 * Mirrors the LCA MFE's feature-flag override at apps/lca-mfe/src/lib/featureFlags.ts.
 * Default is OFF — user asked for Products + Life cycles both back in
 * the sidebar (2026-08-18). URL param `?flag_lca_studies_route=1`
 * opts a single tab back into the unified Studies UI when needed.
 */
function isLcaStudiesRouteOn(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const v = new URLSearchParams(window.location.search).get('flag_lca_studies_route');
    return v === '1' || v?.toLowerCase() === 'true';
  } catch { return false; }
}

function getMenuNavDisplayLabel(menu: Pick<AllowedMenu, 'label' | 'path'> & { customLabel?: string | null }): string {
  // The tenant's own name from Profile → Menu Setup wins; otherwise the label
  // configured in super-admin. Only the displayed text changes: grouping,
  // expansion and permission checks keep using `label`.
  return menu.customLabel?.trim() || menu.label;
}

/**
 * Filter predicate applied when building the sidebar menu tree. Returns
 * false for menus that should be hidden entirely.
 */
function isMenuHiddenByFlag(menu: Pick<AllowedMenu, 'path'>): boolean {
  const p = (menu.path || '').toLowerCase();
  if (isLcaStudiesRouteOn()) {
    // Life cycles is merged into Studies — hide from sidebar.
    if (p === '/lca/projects' || p === '/lca/projects/') return true;
  }
  return false;
}

// Carbon Emissions was on this list until its workspace was split into six
// navigable sub-modules (Dashboard, Data Collection, Scope 1-3, Generate
// Report). Its per-plan button gates live in the parent menu's `features`
// JSON (scope1, ghg_database, set_frequency, ...), NOT in child menu rows,
// so stripping its children only ever hid navigation. Do not re-add it.
//
// Pages whose Edit-Plan sub-items are page actions/buttons (gated per plan),
// NOT navigable sub-pages. Their child menu rows drive the super-admin
// "Edit Plan" tree and per-plan button gating, but must never render as
// sidebar navigation — so we drop their children before building the sidebar.
const ACTION_CONTAINER_PATHS = new Set(['/dashboard', '/help_support', '/assurance', '/green_certification', '/analytics/competitive', '/strategic_roadmap', '/green-loan-book', '/data_reviews/report_audit', '/reporting/reviewandfeedback', '/reporting/generation', '/reporting/sustainability-reports', '/data_collection/fillings', '/data_reviews/reviews_listing', '/communication/chat_and_esgai', '/administration', '/materiality_assessment', '/settings/company_profile', '/supplier-assessments', '/supplier-campaigns', '/supplier-engagement', '/modern-slavery', '/supplier-questionnaires', '/supplier-reports', '/supplier-risk-events', '/supplier-scope3', '/suppliers']);

// dMRV entries use the dMRV "carbon operations" palette (forest green with a
// lime marker) in light mode; every other module keeps the default blue.
const isDmrvPath = (p: string) => { const x = (p || '').toLowerCase(); return x === '/dmrv' || x.startsWith('/dmrv/'); };

// Modules that publish their own section list through Module Federation
// (dMRV: dmrv_mfe/DmrvNav). Their dropdown shows those sections instead of
// the Super Admin child menus; the Super Admin rows still decide visibility.
const SELF_NAVIGATING_MODULE_PATHS = new Set(['/dmrv']);

function stripActionContainerChildren(menus: AllowedMenu[]): AllowedMenu[] {
  return menus.map((m) => {
    const isContainer = ACTION_CONTAINER_PATHS.has((m.path || '').toLowerCase());
    return {
      ...m,
      children: isContainer
        ? []
        : m.children
          ? stripActionContainerChildren(m.children)
          : m.children,
    };
  });
}
/**
 * Supplier Hub gets a "Dashboard" leaf as its first tab.
 *
 * The plan catalog models Supplier Hub as a parent whose OWN path is
 * /supplier-dashboard, with the working pages as children. Since a parent
 * with children is never itself navigable in this sidebar (clicking it opens
 * its first tab), the dashboard page that lives at the parent's path had no
 * way in. Rather than change the shared catalog — which would touch every
 * plan and every tenant — the leaf is synthesised here, for this one module.
 *
 * Confined by path: nothing else in the menu tree is touched, and a catalog
 * that later carries a real Dashboard child wins (the check for an existing
 * child makes this a no-op).
 */
const SUPPLIER_HUB_PARENT_PATHS = new Set(['/supplier-dashboard', '/supplier_dashboard']);

function withSupplierHubDashboardLeaf(menus: AllowedMenu[]): AllowedMenu[] {
  return menus.map((m) => {
    const path = (m.path || '').toLowerCase();
    const kids = m.children ?? [];
    if (!SUPPLIER_HUB_PARENT_PATHS.has(path) || kids.length === 0) return m;
    const already = kids.some((c) => SUPPLIER_HUB_PARENT_PATHS.has((c.path || '').toLowerCase()));
    if (already) return m;
    const minOrder = Math.min(...kids.map((c) => Number(c.sortOrder ?? 0)));
    return {
      ...m,
      children: [
        {
          id: `${m.id}__dashboard`,
          label: 'Dashboard',
          path: m.path,
          parentId: m.id,
          icon: 'LayoutDashboard',
          sortOrder: (Number.isFinite(minOrder) ? minOrder : 0) - 1,
          features: {},
        },
        ...kids,
      ],
    };
  });
}

// Cross-namespace URL → sidebar-leaf overrides. Needed for modules whose
// category tabs jump into unrelated URL segments — e.g. PCF's "Compliance"
// sidebar leaf is /pcf/screening but the category also owns /pcf/evidence,
// /pcf/recalc, /pcf/audit. Longest-prefix alone can't resolve those.
// Kept in sync with each MFE's src/config/categories.ts.
const SUB_PAGE_TO_LEAF: ReadonlyArray<{ prefix: string; leafPath: string }> = [
  // PCF
  { prefix: '/pcf/products',       leafPath: '/pcf/overview' },
  { prefix: '/pcf/reference',      leafPath: '/pcf/overview' },
  { prefix: '/pcf/bom-import',     leafPath: '/pcf/calculator' },
  { prefix: '/pcf/import',         leafPath: '/pcf/calculator' },
  { prefix: '/pcf/allocation',     leafPath: '/pcf/calculator' },
  { prefix: '/pcf/electricity',    leafPath: '/pcf/calculator' },
  { prefix: '/pcf/recalc',         leafPath: '/pcf/screening' },
  { prefix: '/pcf/evidence',       leafPath: '/pcf/screening' },
  { prefix: '/pcf/verification',   leafPath: '/pcf/screening' },
  { prefix: '/pcf/audit',          leafPath: '/pcf/screening' },
  { prefix: '/pcf/mapping-memory', leafPath: '/pcf/review' },
  { prefix: '/pcf/memory',         leafPath: '/pcf/review' },
  { prefix: '/pcf/payloads',       leafPath: '/pcf/exchange' },
  { prefix: '/pcf/twins',          leafPath: '/pcf/exchange' },
  { prefix: '/pcf/dtr',            leafPath: '/pcf/exchange' },
  { prefix: '/pcf/edc',            leafPath: '/pcf/exchange' },
  { prefix: '/pcf/requests',       leafPath: '/pcf/exchange' },
  // ROC
  { prefix: '/roc/calendar',       leafPath: '/roc/overview' },
  { prefix: '/roc/directors',      leafPath: '/roc/companies' },
  { prefix: '/roc/auditors',       leafPath: '/roc/companies' },
  { prefix: '/roc/forms',          leafPath: '/roc/compliances' },
  { prefix: '/roc/tasks',          leafPath: '/roc/compliances' },
  // CCTS
  { prefix: '/ccts/facilities',    leafPath: '/ccts/overview' },
  { prefix: '/ccts/audit',         leafPath: '/ccts/compliance' },
  { prefix: '/ccts/boundary-changes', leafPath: '/ccts/compliance' },
];

const Sidebar = ({ isOpen, onClose: _onClose, forceRender = false, collapsed = false, onToggleCollapse }: SidebarProps) => {
  const { hasLayout } = useLayout();
  const location = useLocation();
  const { user, getAllowedMenus, hasMenuPermission, isInvitedUser } = useAuth();
  // Rows a module has declared meaningless for its selected scope (e.g. CBAM
  // importer-only groups while an operator profile is chosen). Sidebar-only:
  // routing and permissions still see the full tree.
  const isHiddenByModuleScope = useModuleNavScope();
  // Built-in modules whose own look the plan theme switches off ("ESG Default theme") use the standard menu styling.
  // dMRV defaults to the platform (ESG Default) menu styling like every other
  // module — its forest-green chrome is no longer the default. It gets its own
  // (green) menu styling back ONLY when the plan gives dMRV its own colours in
  // Super Admin ("Use these colours for dMRV"), i.e. there is a `/dmrv` entry
  // under `modules`. "ESG Default theme" sets no module colours, so dMRV stays
  // plain. Other built-in modules switched to the default stay plain as before.
  const planThemeVal = (user as { planTheme?: { builtInModules?: { paths: string[]; useDefault?: boolean }[]; modules?: { path?: string; colours?: Record<string, string> }[] } | null } | null)?.planTheme ?? null;
  const dmrvThemed = (planThemeVal?.modules ?? []).some((m) => (m.path || '').toLowerCase() === '/dmrv' && !!m.colours && Object.keys(m.colours).length > 0);
  const plainModulePaths = [
    ...(dmrvThemed ? [] : ['/dmrv']),
    ...((planThemeVal?.builtInModules ?? []).filter((b) => b.useDefault).flatMap((b) => b.paths.map((p) => p.toLowerCase())).filter((p) => p !== '/dmrv')),
  ];
  const dmrvStyled = (p: string) => isDmrvPath(p) && !plainModulePaths.some((mp) => { const x = (p || '').toLowerCase(); return x === mp || x.startsWith(`${mp}/`); });
  const [orgLogoBroken, setOrgLogoBroken] = useState(false);
  const companyLogo = user?.company?.logo || null;
  const orgLogo = orgLogoBroken ? null : companyLogo;
  // A different company (or a newly set logo) gets a fresh chance to load.
  useEffect(() => {
    setOrgLogoBroken(false);
  }, [companyLogo]);

  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const navigate = useNavigate();
  const [categoriesWithCharts, setCategoriesWithCharts] = useState<Set<string>>(new Set());
  const [chartPillars, setChartPillars] = useState<Set<string>>(new Set());
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [categoriesFetched, setCategoriesFetched] = useState(false);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  // Sub-items published by self-navigating modules (dMRV: dmrv_mfe/DmrvNav),
  // keyed by the module's root path. Empty until loaded / when unavailable.
  const [selfNavChildren, setSelfNavChildren] = useState<Record<string, AllowedMenu[]>>({});

  // Ref to preserve scroll position
  const navRef = useRef<HTMLElement>(null);

  // Fetch categories that have charts
  useEffect(() => {
    const fetchCategoriesWithCharts = async () => {
      try {
        setLoadingCategories(true);
        const response = await api.getCategoriesWithCharts();
        if (response.isSuccess && response.data) {
          const categoryNames = new Set(response.data.map(cat => cat.name));
          setCategoriesWithCharts(categoryNames);
          // Each category's "pillar" (Environmental/Social/Governance/Supply
          // Chain) is a coarser grouping than its name (Energy, Community,
          // ...) — the hardcoded Analytics bundle below matches on pillar,
          // not on the fine-grained category name.
          const pillars = new Set(
            response.data.map(cat => cat.pillar).filter((p): p is string => !!p),
          );
          setChartPillars(pillars);
          setCategoriesFetched(true);
        }
      } catch (error) {
        console.error('[Sidebar] Failed to fetch categories with charts:', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategoriesWithCharts();
  }, []);

  // Fetch pending review count
  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const response = await api.getAuditStats();
        if (response.isSuccess && response.data) {
          const stats = (response.data as { data?: { pendingReviews?: number }; pendingReviews?: number }).data ?? response.data;
          setPendingReviewCount(stats?.pendingReviews ?? 0);
        }
      } catch {
        // Silent fail
      }
    };
    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 60000);
    return () => clearInterval(interval);
  }, []);

  // Save scroll position before navigation (using sessionStorage for persistence)
  const saveScrollPosition = () => {
    if (navRef.current) {
      sessionStorage.setItem('sidebarScrollPosition', String(navRef.current.scrollTop));
    }
  };

  // Restore scroll position after component mounts and on route change
  useEffect(() => {
    // Skip if we're not rendering
    if (hasLayout && !forceRender) return;

    const restoreScroll = () => {
      const savedPosition = sessionStorage.getItem('sidebarScrollPosition');
      if (navRef.current && savedPosition) {
        navRef.current.scrollTop = parseInt(savedPosition, 10);
      }
    };

    // Use multiple attempts with increasing delays to handle lazy loading
    // First attempt immediately
    restoreScroll();

    // Second attempt after a short delay (for fast loads)
    const timeout1 = setTimeout(restoreScroll, 50);

    // Third attempt after lazy component loads
    const timeout2 = setTimeout(restoreScroll, 150);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
    };
  }, [location.pathname, hasLayout, forceRender]);

  // Also save scroll position on scroll (debounced)
  useEffect(() => {
    // Skip if we're not rendering
    if (hasLayout && !forceRender) return;

    const nav = navRef.current;
    if (!nav) return;

    let timeoutId: NodeJS.Timeout;
    const handleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        sessionStorage.setItem('sidebarScrollPosition', String(nav.scrollTop));
      }, 100);
    };

    nav.addEventListener('scroll', handleScroll);
    return () => {
      nav.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, [hasLayout, forceRender]);

  // If layout is already provided by AppLayout and we're not forcing render, skip
  // This prevents duplicate sidebars when pages still have old layout code
  if (hasLayout && !forceRender) {
    return null;
  }

  // Get user display name and initials
  const userName = user ? formatName(user.firstName, user.lastName) : 'User';
  const userInitials = user ? getInitials(user.firstName, user.lastName) : 'U';
  const userRole = user?.jobTitle || user?.role || 'Team Member';

  // Get allowed menus from context - ONLY use API menus, no defaults
  const apiMenus = getAllowedMenus();

  // Use API menus only - no fallback to defaults
  // This ensures sidebar shows ONLY what super-admin assigned
  const allowedMenus = apiMenus;

  // Load dMRV's own section list only when dMRV is in this user's menu. If the
  // remote can't be reached, dMRV simply shows as a single link.
  const hasDmrvMenu = (allowedMenus ?? []).some((m) => (m.path || '').toLowerCase() === '/dmrv');
  // dMRV sections depend on the category workspace the user is in, which they can
  // change at any time. The remote announces a change on 'dmrv:workspace-changed';
  // we then ask it for its sections again. Nothing else in the sidebar is involved.
  const [dmrvNavVersion, setDmrvNavVersion] = useState(0);
  useEffect(() => {
    const refresh = () => setDmrvNavVersion((v) => v + 1);
    window.addEventListener('dmrv:workspace-changed', refresh);
    return () => window.removeEventListener('dmrv:workspace-changed', refresh);
  }, []);
  // Loaded once per workspace version; the ref (not selfNavChildren) is what stops
  // it re-running, so setting the sections cannot feed back into this effect.
  const dmrvNavLoaded = useRef(-1);
  useEffect(() => {
    if (!hasDmrvMenu || dmrvNavLoaded.current === dmrvNavVersion) return;
    dmrvNavLoaded.current = dmrvNavVersion;
    let cancelled = false;
    import('dmrv_mfe/DmrvNav')
      .then((mod) => {
        if (cancelled) return;
        // getDmrvSections() is re-evaluated per call; DMRV_SECTIONS is a load-time
        // snapshot, kept as the fallback for an older remote build.
        const live = typeof (mod as any).getDmrvSections === 'function' ? (mod as any).getDmrvSections() : null;
        const list = live ?? mod.default ?? mod.DMRV_SECTIONS ?? [];
        setSelfNavChildren((prev) => ({
          ...prev,
          '/dmrv': list.map((s) => ({ id: s.id, label: s.label, path: s.path, parentId: null, icon: s.icon, sortOrder: s.sortOrder, features: {} })),
        }));
      })
      .catch((err) => console.warn('[Sidebar] dMRV section list unavailable:', err));
    return () => { cancelled = true; };
  }, [hasDmrvMenu, dmrvNavVersion]);

  // Check if an invited user has permission for a menu path
  const checkMenuPermission = (path: string): boolean => {
    // For non-invited users (tenant-admin), allow all menus from plan
    if (!isInvitedUser()) {
      return true;
    }
    // For invited users, check role permissions
    return hasMenuPermission(path);
  };

  // Dynamically match menu label to a category name from the DB.
  // Normalizes "&" / "and", casing, and extra spaces for flexible matching.
  const normalize = (s: string) => s.toLowerCase().replace(/&/g, 'and').replace(/\s+/g, ' ').trim();
  const getCategoryFromLabel = (label: string): string | null => {
    const labelNorm = normalize(label);
    for (const catName of categoriesWithCharts) {
      if (normalize(catName) === labelNorm) return catName;
    }
    return null;
  };

  // Check if a menu is a category page (child of Environmental/Social/Governance/Supply Chain)
  const isCategoryPage = (menu: AllowedMenu): boolean => {
    const p = (menu.path || '').toLowerCase();
    return p.includes('/environmental/') || p.includes('/social/') ||
           p.includes('/governance/') || p.includes('/supply-chain/') ||
           p.includes('/supply_chain/');
  };

  // Check if a menu should be shown based on category chart availability
  const shouldShowMenu = (menu: AllowedMenu): boolean => {
    const categoryName = getCategoryFromLabel(menu.label);

    // If this menu maps to a category with charts, show it once chart list has loaded
    if (categoryName) {
      return !loadingCategories;
    }

    // ESG category leaves: hide only when the chart-category fetch succeeded AND
    // there are charts configured (some other category has charts) but this one doesn't.
    // If no charts are configured at all (empty tenant), show everything.
    if (isCategoryPage(menu)) {
      if (loadingCategories) return true;
      if (!categoriesFetched) return true;
      if (categoriesWithCharts.size === 0) return true;
      return false;
    }

    return true;
  };

  // Build menu sections from hierarchical data
  const menuSections = useMemo(() => {
    if (!allowedMenus || allowedMenus.length === 0) {
      return [];
    }

    // Recursive function to process, filter and sort menus at all levels
    // For invited users, filter out menus they don't have permission for
    const processMenuChildren = (menus: AllowedMenu[]): (AllowedMenu & { _hadChildren?: boolean })[] => {
      return menus
        .map(menu => {
          const hadChildren = !!(menu.children && menu.children.length > 0);
          // Recursively process children first
          const processedChildren = hadChildren
            ? processMenuChildren(menu.children!)
            : [];

          return {
            ...menu,
            children: processedChildren,
            _hadChildren: hadChildren,
          };
        })
        // Filter rules:
        // 1. If a menu originally had children but all got filtered out â†’ hide the parent too
        // 2. Parent with visible children â†’ show
        // 3. Leaf menu â†’ show only if user has permission AND (if it's an ESG category) a chart exists for it
        .filter(menu => {
          // LCA new-UI: hide entries superseded by /lca/studies.
          if (isMenuHiddenByFlag(menu)) return false;
          if (isHiddenByModuleScope(menu.path)) return false;
          const hasVisibleChildren = menu.children && menu.children.length > 0;
          if (menu._hadChildren && !hasVisibleChildren) {
            return false;
          }
          if (hasVisibleChildren) {
            return true;
          }
          return checkMenuPermission(menu.path) && shouldShowMenu(menu);
        })
        .sort(compareAllowedMenusBySortOrder);
    };

    // Group menus: Root menus (parentId === null) become sections
    // Menus with children become expandable accordions
    // Standalone menus become direct links
    const sections: MenuSection[] = [];

    // Drop action-container children (e.g. Dashboard/Help & Support button rows)
    // so those pages render as plain links, not expandable nav groups.
    const navMenus = withSupplierHubDashboardLeaf(stripActionContainerChildren(allowedMenus));

    // Sort menus by sortOrder first
    const sortedMenus = [...navMenus].sort(compareAllowedMenusBySortOrder);

    for (const menu of sortedMenus) {
      const hasChildren = menu.children && menu.children.length > 0;

      // Recursively process, filter and sort children at all levels
      const allowedChildren = hasChildren
        ? processMenuChildren(menu.children!)
        : [];

      // For root-level menus:
      // - If it has children, show it only if it has visible children
      // - If it's a standalone menu (no children), check permission AND chart availability
      const shouldShowThisMenu = !isHiddenByModuleScope(menu.path) && (hasChildren
        ? allowedChildren.length > 0
        : checkMenuPermission(menu.path) && shouldShowMenu(menu));

      if (shouldShowThisMenu) {
        // Self-navigating modules (dMRV): visibility still follows their Super
        // Admin children (plan entitlements), but the dropdown items are the
        // module's own published section list, not the Super Admin menu rows.
        const rootPath = (menu.path || '').toLowerCase();
        const selfNavigating = SELF_NAVIGATING_MODULE_PATHS.has(rootPath);
        const ownChildren = selfNavigating
          ? (selfNavChildren[rootPath] ?? []).map((c) => ({ ...c, parentId: menu.id }))
          : allowedChildren;
        sections.push({
          id: menu.id,
          label: menu.label,
          customLabel: menu.customLabel ?? null,
          path: menu.path,
          icon: menu.icon || null,
          isExpandable: ownChildren.length > 0,
          children: ownChildren,
        });
      }
    }

    return sections;
  }, [allowedMenus, isInvitedUser, hasMenuPermission, categoriesWithCharts, chartPillars, loadingCategories, categoriesFetched, selfNavChildren, isHiddenByModuleScope]);

  // Auto-expand parent accordions when a nested child route is active (supports deep nesting)
  useEffect(() => {
    const currentPath = location.pathname.toLowerCase();

    // Helper to recursively find all parent labels that need to be expanded
    const findParentLabelsToExpand = (menus: AllowedMenu[], parentLabels: string[] = []): string[] => {
      const labelsToExpand: string[] = [];

      for (const menu of menus) {
        const menuPath = normalizePath(menu.path).toLowerCase();
        const hasChildren = menu.children && menu.children.length > 0;

        if (currentPath === menuPath) {
          // This menu is active - expand all parents
          labelsToExpand.push(...parentLabels);
        }

        if (hasChildren) {
          // Recursively check children
          const childLabels = findParentLabelsToExpand(
            menu.children!,
            [...parentLabels, menu.label]
          );
          labelsToExpand.push(...childLabels);
        }
      }

      return labelsToExpand;
    };

    // Check all sections
    const labelsToExpand = new Set<string>();
    for (const section of menuSections) {
      if (section.isExpandable) {
        const childLabels = findParentLabelsToExpand(section.children, [section.label]);
        childLabels.forEach(label => labelsToExpand.add(label));
      }
    }

    // Expand any labels that aren't already expanded
    if (labelsToExpand.size > 0) {
      setExpandedGroups(prev => {
        const newLabels = [...labelsToExpand].filter(label => !prev.includes(label));
        return newLabels.length > 0 ? [...prev, ...newLabels] : prev;
      });
    }
  }, [location.pathname, menuSections]);

  const normalizePath = (path: string) => {
    if (!path) return '/';
    const withSlash = path.startsWith('/') ? path : `/${path}`;
    // Display canonical hyphenated URL for the reporting sub-module.
    return withSlash.replace(/\/reviewandfeedback(\/|$)/i, '/review-and-feedback$1');
  };

  const allLeafPaths = useMemo(() => {
    const paths: string[] = [];
    const collect = (menus: AllowedMenu[]) => {
      for (const m of menus) {
        const hasKids = m.children && m.children.length > 0;
        if (hasKids) collect(m.children!);
        else paths.push(normalizePath(m.path).toLowerCase());
      }
    };
    for (const section of menuSections) {
      if (section.isExpandable) collect(section.children);
      else paths.push(normalizePath(section.path).toLowerCase());
    }
    return paths;
  }, [menuSections]);

  // Longest-prefix match so sub-pages like /ccts/ledger/balance still
  // highlight the /ccts/ledger leaf. Longest wins over a shorter parent leaf.
  // Falls back to SUB_PAGE_TO_LEAF for cross-namespace overrides.
  const activeLeafPath = useMemo(() => {
    const current = location.pathname.toLowerCase();
    let best = '';
    for (const p of allLeafPaths) {
      if (current === p || current.startsWith(p + '/')) {
        if (p.length > best.length) best = p;
      }
    }
    if (best) return best;
    for (const m of SUB_PAGE_TO_LEAF) {
      if (current === m.prefix || current.startsWith(m.prefix + '/')) {
        const target = m.leafPath.toLowerCase();
        if (allLeafPaths.includes(target)) return target;
      }
    }
    return '';
  }, [allLeafPaths, location.pathname]);

  const isActive = (path: string) => {
    const menuPath = path.toLowerCase();
    if (activeLeafPath && menuPath === activeLeafPath) return true;
    return location.pathname.toLowerCase() === menuPath;
  };

  const toggleGroup = (groupLabel: string) => {
    setExpandedGroups(prev =>
      prev.includes(groupLabel)
        ? prev.filter(g => g !== groupLabel)
        : [...prev, groupLabel]
    );
  };

  /**
   * The first page a parent module can actually open.
   *
   * Depth-first, because a module's first child may itself be a group
   * ("Environmental > Energy Management > Overview") and only a LEAF has a page
   * to show. A node with children is never itself navigable in this sidebar —
   * it renders as an accordion, not a link — so descending is the only way to
   * reach something real.
   *
   * Returns null when a branch holds no leaf with a path, which happens when
   * permissions have filtered every child out. The caller then falls back to
   * expanding only, rather than navigating somewhere that does not exist.
   */
  const firstLeafPath = (menu: AllowedMenu): string | null => {
    const kids = menu.children ?? [];
    if (kids.length === 0) {
      return menu.path ? normalizePath(menu.path) : null;
    }
    for (const child of kids) {
      const found = firstLeafPath(child);
      if (found) return found;
    }
    return null;
  };

  /**
   * Clicking a module NAME opens its first page instead of only unfolding a
   * list. Unfolding alone made the label a dead control: the user had to click
   * the module, read the children, then click again to get anywhere.
   *
   * The label always EXPANDS (never collapses) — collapsing while navigating
   * into the section would hide the page you just opened. The chevron beside it
   * stays a real toggle, so closing a section is still one click.
   */
  const openModule = (menu: AllowedMenu) => {
    const target = firstLeafPath(menu);
    setExpandedGroups(prev =>
      prev.includes(menu.label) ? prev : [...prev, menu.label],
    );
    if (target) {
      saveScrollPosition();
      navigate(target);
    }
  };

  // Render a single menu item (link)
  const renderMenuItem = (menu: AllowedMenu, isNested: boolean = false) => {
    const Icon = getIcon(menu.icon || menu.label);
    const menuPath = normalizePath(menu.path);
    const colorClass = getCategoryColorFromPath(menuPath);
    const active = isActive(menuPath);

    return (
      <li key={menu.id}>
        <Link
          to={menuPath}
          onClick={saveScrollPosition}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
            isNested ? 'pl-6 text-[13px]' : ''
          } ${
            active
              ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
          }`}
        >
          {!isNested && <Icon className={`w-5 h-5 flex-shrink-0 ${colorClass}`} />}
          <span className="flex-1">{getMenuNavDisplayLabel(menu)}</span>
          {pendingReviewCount > 0 && isReviewMenu(menu.label, menuPath) && (
            <span className="ml-auto min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-bold">
              {pendingReviewCount > 99 ? '99+' : pendingReviewCount}
            </span>
          )}
        </Link>
      </li>
    );
  };

  // Identify the Pending Reviews menu strictly â€” match label first, then exact
  // path suffix. Avoids false positives from loose substring matches like
  // "/data_reviews" appearing as a parent segment of unrelated routes.
  const isReviewMenu = (label: string, path: string): boolean => {
    const l = (label || '').toLowerCase().trim();
    if (l === 'pending reviews' || l === 'pending review') return true;
    const p = (path || '').toLowerCase().replace(/\/+$/, '');
    return p.endsWith('/pending_reviews') || p.endsWith('/pending-reviews') ||
           p.endsWith('/reviews_listing') || p.endsWith('/review_listing') ||
           p.endsWith('/review-listing');
  };

  // Helper to check if any menu or its descendants are active
  const hasActiveDescendant = (menu: AllowedMenu): boolean => {
    if (isActive(normalizePath(menu.path))) return true;
    if (menu.children && menu.children.length > 0) {
      return menu.children.some(child => hasActiveDescendant(child));
    }
    return false;
  };

  // Recursive render for nested menu items (handles unlimited depth)
  const renderNestedMenuItem = (menu: AllowedMenu, depth: number = 1): React.ReactNode => {
    const menuPath = normalizePath(menu.path);
    const hasChildren = menu.children && menu.children.length > 0;
    const isMenuExpanded = expandedGroups.includes(menu.label);
    const paddingLeft = `${depth * 12 + 12}px`; // Increase indent for each level

    if (hasChildren) {
      // This menu has children - render as expandable sub-section
      const hasActiveChild = hasActiveDescendant(menu);
      const SubIcon = depth === 1 ? getIcon(menu.icon || menu.label) : null;
      return (
        <li key={menu.id}>
          <div
            className={`w-full flex items-center rounded-lg text-[13px] font-medium transition-all ${
              hasActiveChild
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-slate-500 dark:text-slate-400'
            } hover:bg-slate-100 dark:hover:bg-slate-800`}
          >
            {/* The name opens the section's first page. */}
            <button
              onClick={() => openModule(menu)}
              className="flex-1 flex items-center gap-2 px-3 py-2 min-w-0 text-left"
              style={{ paddingLeft }}
            >
              {SubIcon && <SubIcon className={`w-4 h-4 flex-shrink-0 flex-none ${getCategoryColorFromPath(menuPath)}`} />}
              <span className="flex-1 text-left truncate">{getMenuNavDisplayLabel(menu)}</span>
            </button>
            {/* The chevron stays a plain toggle, so a section can still be
                closed without navigating anywhere. Sibling, not nested — a
                button inside a button is invalid HTML. */}
            <button
              onClick={() => toggleGroup(menu.label)}
              aria-label={isMenuExpanded ? `Collapse ${menu.label}` : `Expand ${menu.label}`}
              aria-expanded={isMenuExpanded}
              className="px-2 py-2 shrink-0"
            >
              <ChevronDown className={`w-3 h-3 transition-transform ${isMenuExpanded ? 'rotate-180' : ''}`} />
            </button>
          </div>
          <div
            className={`overflow-hidden transition-all duration-200 ${
              isMenuExpanded ? 'max-h-[2000px]' : 'max-h-0'
            }`}
          >
            <ul className="space-y-0.5">
              {menu.children!.map((child) => renderNestedMenuItem(child, depth + 1))}
            </ul>
          </div>
        </li>
      );
    }

    // This is a leaf menu item - render as link
    const LeafIcon = depth === 1 ? getIcon(menu.icon || menu.label) : null;
    const dmrvActive = dmrvStyled(menuPath) && isActive(menuPath);
    return (
      <li key={menu.id}>
        <Link
          to={menuPath}
          onClick={saveScrollPosition}
          className={`relative flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] font-medium transition-all ${
            dmrvActive
              ? 'bg-[#2c5a45] text-[#f6f5ee] shadow-sm dark:bg-blue-900/30 dark:text-blue-400'
              : isActive(menuPath)
              ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
              : dmrvStyled(menuPath)
              ? 'text-[#5f6b62] hover:bg-[#e2e6da] dark:text-slate-400 dark:hover:bg-slate-800'
              : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
          }`}
          style={{ paddingLeft }}
        >
          {LeafIcon && <LeafIcon className={`w-4 h-4 flex-shrink-0 ${dmrvActive ? 'text-[#d4e09b] dark:text-blue-400' : dmrvStyled(menuPath) ? 'text-[#6b7266] dark:text-slate-400' : getCategoryColorFromPath(menuPath)}`} />}
          <span className="flex-1">{getMenuNavDisplayLabel(menu)}</span>
          {dmrvActive && <span className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-[#d4e09b]" aria-hidden="true" />}
          {pendingReviewCount > 0 && isReviewMenu(menu.label, menuPath) && (
            <span className="ml-auto min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-bold">
              {pendingReviewCount > 99 ? '99+' : pendingReviewCount}
            </span>
          )}
        </Link>
      </li>
    );
  };

  // Render an expandable section with children (supports recursive nesting)
  const renderExpandableSection = (section: MenuSection) => {
    const Icon = getIcon(section.icon || section.label);
    const sectionPath = normalizePath(section.path);
    const colorClass = getCategoryColorFromPath(sectionPath);
    const isExpanded = expandedGroups.includes(section.label);

    // Check if any descendant (child, grandchild, etc.) is active
    const hasActiveChild = section.children.some(child => hasActiveDescendant(child));

    const dmrvSection = dmrvStyled(sectionPath);
    return (
      <li key={section.id}>
        <div className={dmrvSection ? 'rounded-xl bg-[#eceee6] p-1 dark:bg-transparent dark:p-0' : undefined}>
          <div
            className={`w-full flex items-center rounded-lg text-sm font-medium transition-all ${
              hasActiveChild
                ? (dmrvSection ? 'text-[#2c5a45] font-semibold dark:text-blue-400' : 'text-blue-600 dark:text-blue-400')
                : dmrvSection ? 'text-[#4b5a50] dark:text-slate-400' : 'text-slate-600 dark:text-slate-400'
            } ${dmrvSection ? 'hover:bg-[#e2e6da]' : 'hover:bg-slate-100'} dark:hover:bg-slate-800`}
          >
            {/* The module name opens its first page rather than only unfolding
                the list — see openModule. */}
            <button
              onClick={() => openModule(section as unknown as AllowedMenu)}
              className="flex-1 flex items-center gap-3 px-3 py-2.5 min-w-0 text-left"
            >
              <Icon className={`w-5 h-5 flex-shrink-0 ${dmrvSection ? 'text-[#2c5a45] dark:text-slate-400' : colorClass}`} />
              <span className="flex-1 text-left truncate">{getMenuNavDisplayLabel(section)}</span>
            </button>
            <button
              onClick={() => toggleGroup(section.label)}
              aria-label={isExpanded ? `Collapse ${section.label}` : `Expand ${section.label}`}
              aria-expanded={isExpanded}
              className="px-2 py-2.5 shrink-0"
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
          </div>
          <div
            className={`overflow-hidden transition-all duration-200 ${
              isExpanded ? 'max-h-[2000px]' : 'max-h-0'
            }`}
          >
            <ul className="py-1 pl-2 space-y-0.5">
              {section.children.map((child) => renderNestedMenuItem(child, 1))}
            </ul>
          </div>
        </div>
      </li>
    );
  };

  // Render a single menu item as icon-only with tooltip (collapsed mode)
  const renderCollapsedMenuItem = (menu: AllowedMenu) => {
    const Icon = getIcon(menu.icon || menu.label);
    const menuPath = normalizePath(menu.path);
    const colorClass = getCategoryColorFromPath(menuPath);
    const active = isActive(menuPath);

    return (
      <li key={menu.id} className="w-full">
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to={menuPath}
              onClick={saveScrollPosition}
              className={`flex items-center justify-center w-12 h-10 mx-auto rounded-lg transition-all ${
                active
                  ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              <Icon className={`w-5 h-5 flex-shrink-0 ${colorClass}`} />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right">{getMenuNavDisplayLabel(menu)}</TooltipContent>
        </Tooltip>
      </li>
    );
  };

  // Render an expandable section as icon + dropdown (collapsed mode)
  const renderCollapsedExpandableSection = (section: MenuSection) => {
    const Icon = getIcon(section.icon || section.label);
    const sectionPath = normalizePath(section.path);
    const colorClass = getCategoryColorFromPath(sectionPath);
    const hasActiveChild = section.children.some(child => hasActiveDescendant(child));

    // Flatten children recursively for the dropdown
    const flattenChildren = (items: AllowedMenu[]): AllowedMenu[] => {
      const result: AllowedMenu[] = [];
      for (const item of items) {
        if (item.children && item.children.length > 0) {
          result.push(...flattenChildren(item.children));
        } else {
          result.push(item);
        }
      }
      return result;
    };
    const flatChildren = flattenChildren(section.children);

    return (
      <li key={section.id} className="w-full">
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button
                  className={`flex items-center justify-center w-12 h-10 mx-auto rounded-lg transition-all ${
                    hasActiveChild
                      ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${colorClass}`} />
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="right">{getMenuNavDisplayLabel(section)}</TooltipContent>
          </Tooltip>
          <DropdownMenuContent side="right" align="start" className="min-w-[180px]">
            {flatChildren.map(child => {
              const childPath = normalizePath(child.path);
              return (
                <DropdownMenuItem key={child.id} asChild>
                  <Link
                    to={childPath}
                    onClick={saveScrollPosition}
                    className={`w-full ${
                      isActive(childPath) ? 'text-blue-600 dark:text-blue-400 font-medium' : ''
                    }`}
                  >
                    {getMenuNavDisplayLabel(child)}
                  </Link>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </li>
    );
  };

  // Helper function to determine category from path
  const getCategoryFromPath = (path: string): { title: string; sortOrder: number } => {
    const normalizedPath = path.toLowerCase();

    // Dashboard - no header, appears first
    if (normalizedPath === '/dashboard' || normalizedPath === 'dashboard') {
      return { title: '', sortOrder: 0 };
    }

    // Analytics section
    if (normalizedPath.includes('/analytics') || normalizedPath.includes('/overview') ||
        normalizedPath.includes('/environmental') || normalizedPath.includes('/social') ||
        normalizedPath.includes('/governance') || normalizedPath.includes('/supply-chain') ||
        normalizedPath.includes('/supply_chain') || normalizedPath.includes('/actions') ||
        normalizedPath.includes('/competitive')) {
      return { title: 'ANALYTICS', sortOrder: 1 };
    }

    // Assessment section â€” LCA + PCF live here (both are lifecycle carbon
    // assessments), alongside materiality, roadmap, net-zero, assurance.
    if (normalizedPath.includes('/assessment') || normalizedPath.includes('/materiality') ||
        normalizedPath.includes('/strategic_roadmap') || normalizedPath.includes('/esg-roadmap') ||
        normalizedPath.includes('/emission-modelling') || normalizedPath.includes('/emission_modelling') ||
        normalizedPath.includes('/complaints') || normalizedPath.includes('/complaint') ||
        normalizedPath.includes('/grievance') || normalizedPath.includes('/grievances') ||
        normalizedPath.includes('/net-zero') || normalizedPath.includes('/net_zero') ||
        normalizedPath.includes('/assurance') || normalizedPath.includes('/lca') ||
        normalizedPath === '/pcf' || normalizedPath.startsWith('/pcf/') ||
        normalizedPath === '/dmrv' || normalizedPath.startsWith('/dmrv/') || // dMRV (carbon-removal MRV) sits with the other assessments
        normalizedPath === '/cbam' || normalizedPath.startsWith('/cbam/') || // CBAM (carbon border adjustment)
        normalizedPath.includes('/target-data') || normalizedPath.includes('/target_data') ||
        normalizedPath.includes('/target_and_action') || normalizedPath.includes('/target-and-action')) {
      return { title: 'ASSESSMENT', sortOrder: 1.5 };
    }

    // Sustainability section â€” CCTS (Carbon Credit Trading Scheme) lives here
    // alongside green certification and the green loan book.
    if (normalizedPath.includes('/green-certification') || normalizedPath.includes('/green_certification') ||
        normalizedPath.includes('/green-loan') || normalizedPath.includes('/green_loan') ||
        normalizedPath === '/ccts' || normalizedPath.startsWith('/ccts/') ||
        normalizedPath === '/rco-energy' || normalizedPath.startsWith('/rco-energy/') ||
        // Water & Effluent and Waste & Compliance are operational compliance
        // modules — same family as RCO — so they belong under SUSTAINABILITY,
        // not the generic Reporting bucket (they aren't reports).
        normalizedPath === '/water' || normalizedPath.startsWith('/water/') ||
        normalizedPath === '/waste' || normalizedPath.startsWith('/waste/') ||
        (normalizedPath.includes('/sustainability') && !normalizedPath.includes('/sustainability-reports'))) {
      return { title: 'SUSTAINABILITY', sortOrder: 1.7 };
    }

    // Reporting section â€” XBRL Support (disclosure format) and ROC
    // (Registrar of Companies regulatory filings: MGT-7, AOC-4, etc.)
    // belong alongside sustainability reports and report generation.
    if (normalizedPath.includes('/reporting') || normalizedPath.includes('/report') ||
        normalizedPath.includes('/generation') || normalizedPath.includes('/filing') ||
        normalizedPath.includes('/sustainability-reports') ||
        normalizedPath.includes('/xbrl') ||
        normalizedPath === '/roc' || normalizedPath.startsWith('/roc/') ||
        normalizedPath === '/compliance' || normalizedPath.startsWith('/compliance/')) {
      return { title: 'REPORTING', sortOrder: 2 };
    }

    // Data Collection section (includes Pending Reviews)
    if (normalizedPath.includes('/data-collection') || normalizedPath.includes('/carbon-emission') ||
        normalizedPath.includes('/carbon_emission') || normalizedPath.includes('/fillings') ||
        normalizedPath.includes('/review') || normalizedPath.includes('/reviews') ||
        normalizedPath.includes('/data_reviews') || normalizedPath.includes('/pending_reviews') ||
        normalizedPath.includes('/pending-reviews') ||
        normalizedPath.includes('/supplier-dashboard') || normalizedPath.includes('/supplier_dashboard') ||
        // Connectors pull data in from external systems (HRIS/ERP/utility)
        // — same family as the manual data-entry screens, so it sits with
        // Framework Question rather than in the OTHER catch-all.
        normalizedPath === '/connectors' || normalizedPath.startsWith('/connectors/')) {
      return { title: 'DATA COLLECTION', sortOrder: 3 };
    }

    // Documents section — the cross-module Document Repository gets its own
    // heading directly above Settings instead of landing in the OTHER catch-all.
    if (/^\/?documents(\/|$)/.test(normalizedPath)) {
      return { title: 'DOCUMENTS', sortOrder: 6.5 };
    }

    // Communication section
    if (normalizedPath.includes('/communication') || normalizedPath.includes('/chat') ||
        normalizedPath.includes('/messages') || normalizedPath.includes('/esg-ai')) {
      return { title: 'COMMUNICATION', sortOrder: 5 };
    }

    // Settings section â€” MUST run before Administration: paths like /settings/... can contain
    // the substring "/admin" (e.g. /settings/admin-help) and would otherwise be mis-bucketed.
    if (normalizedPath.includes('/settings') || normalizedPath.includes('/company-profile') ||
        normalizedPath.includes('/company_profile') || /(\/|^)team(\/|$)/.test(normalizedPath) ||
        normalizedPath.includes('/permission') || normalizedPath.includes('/integration') ||
        normalizedPath.includes('/help') || normalizedPath.includes('/support')) {
      return { title: 'SETTINGS', sortOrder: 7 };
    }

    // Administration section
    if (normalizedPath.includes('/admin') || normalizedPath.includes('/administration')) {
      return { title: 'ADMINISTRATION', sortOrder: 6 };
    }

    // Other section (default)
    return { title: 'OTHER', sortOrder: 8 };
  };

  // Group sections by category based on path
  const groupedSections = useMemo(() => {
    const groupsMap = new Map<string, { title: string; sortOrder: number; items: MenuSection[] }>();

    for (const section of menuSections) {
      // Determine category from path
      const { title: categoryTitle, sortOrder } = getCategoryFromPath(section.path);

      if (!groupsMap.has(categoryTitle)) {
        groupsMap.set(categoryTitle, {
          title: categoryTitle,
          sortOrder: sortOrder,
          items: [],
        });
      }

      groupsMap.get(categoryTitle)!.items.push(section);
    }

    // Convert to array and sort by sortOrder
    const groups = Array.from(groupsMap.values())
      .sort((a, b) => a.sortOrder - b.sortOrder);

    return groups;
  }, [menuSections]);

  return (
    <>
      {/* Collapse toggle button (desktop only) - outside sidebar to avoid overflow-hidden clipping */}
      <button
        onClick={onToggleCollapse}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className="hidden lg:flex fixed top-[72px] z-[102] w-7 h-7 rounded-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 shadow-md items-center justify-center text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-400 transition-all"
        style={{ left: collapsed ? '58px' : '246px' }}
      >
        {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
      </button>

      {/* Sidebar */}
      <aside
        data-theme-part="left-menu"
        className={`fixed top-0 left-0 h-screen ${collapsed ? 'lg:w-[72px]' : 'lg:w-[260px]'} w-[260px] bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 z-[100] flex flex-col transition-[width,transform] duration-300 ease-out shadow-sm overflow-hidden
          ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >

        {/* Header */}
        <div
          className={`border-b border-slate-200 dark:border-slate-700 ${
            collapsed
              ? orgLogo ? 'px-3 pt-3 pb-0' : 'p-3'
              : orgLogo ? 'px-5 pt-5 pb-0' : 'p-5'
          }`}
        >
          <Link to="/dashboard" className={`flex items-center gap-2 ${collapsed ? 'justify-center' : ''}`}>
            <img
              src="/logo.png"
              alt="DataCaffe Logo"
              className={`${collapsed ? 'w-11 h-11' : 'w-14 h-14'} object-contain flex-shrink-0`}
            />
            {!collapsed && (
              <div className="flex flex-col">
                <span className="text-xl font-bold tracking-tight">
                  <span style={{ color: '#f97316' }}>ESG</span><span className="text-blue-600 dark:text-blue-400">Caffe</span>
                </span>
                <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                  Green Intelligence
                </span>
              </div>
            )}
          </Link>

          {/*
            The tenant's own logo, directly beneath the product one and spanning
            the same width, so the two read as one stacked header rather than a
            logo with something tacked beside it. No divider between them — the
            header's own bottom border falls UNDER this, so the line separates
            both marks from the module list below.

            Rendered only when there is a logo: an empty band under the product
            mark reads as a broken image.
          */}
          {orgLogo && (
            <div
              className={`flex items-center ${
                collapsed
                  ? 'mt-1 justify-center w-full'
                  : 'justify-start -mx-2 w-[calc(100%+1rem)] overflow-hidden'
              }`}
            >
              <img
                src={orgLogo}
                alt={user?.company?.name ? `${user.company.name} logo` : 'Organisation logo'}
                title={user?.company?.name || undefined}
                /*
                  The height cap is what decides the rendered WIDTH here: with
                  object-contain a wide mark hits the cap first and is scaled
                  down, so ANY cap tight enough to bind both shrinks the logo
                  below the width it was given and letterboxes it — which is
                  what produced a small, centred mark with dead space beneath.
                  The cap is deliberately loose so width drives the size and
                  the logo fills the header edge to edge; object-left keeps a
                  mark with its own internal padding hard against the left,
                  lined up with the DataCaffe icon above.
                */
                className={`h-auto object-contain object-left ${
                  collapsed ? 'w-11 max-h-11' : 'w-full max-h-28 -mt-2 -mb-2'
                }`}
                /* A logo whose URL has gone stale would otherwise leave a gap
                   with no clue why — drop the whole band instead. */
                onError={() => setOrgLogoBroken(true)}
              />
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav ref={navRef} className={`flex-1 ${collapsed ? 'p-2' : 'p-3'} overflow-y-auto custom-scrollbar`} style={{ scrollBehavior: 'auto', overscrollBehavior: 'contain' }}>
          {groupedSections.length > 0 ? (
            groupedSections.map((group, index) => (
              <div key={group.title || `group-${index}`} className="mb-4">
                {group.title && !collapsed && (
                  <div className="px-3 py-2 text-[11px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                    {group.title}
                  </div>
                )}
                {collapsed && group.title && (
                  <div className="border-b border-slate-200 dark:border-slate-700 mx-2 mb-2" />
                )}
                <ul className={`${collapsed ? 'space-y-1 flex flex-col items-center' : 'space-y-1'}`}>
                  {group.items.map((section) =>
                    collapsed
                      ? (section.isExpandable
                          ? renderCollapsedExpandableSection(section)
                          : renderCollapsedMenuItem({
                              id: section.id,
                              label: section.label,
                              customLabel: section.customLabel ?? null,
                              path: section.path,
                              parentId: null,
                              icon: section.icon,
                              sortOrder: 0,
                              features: {},
                            }))
                      : (section.isExpandable
                          ? renderExpandableSection(section)
                          : renderMenuItem({
                              id: section.id,
                              label: section.label,
                              customLabel: section.customLabel ?? null,
                              path: section.path,
                              parentId: null,
                              icon: section.icon,
                              sortOrder: 0,
                              features: {},
                            }))
                  )}
                </ul>
              </div>
            ))
          ) : (
            // Fallback: Show loading or empty state
            <div className="px-3 py-4 text-sm text-slate-500 dark:text-slate-400">
              {allowedMenus.length === 0 ? (
                <span>Loading menus...</span>
              ) : (
                <span>No menus available</span>
              )}
            </div>
          )}
        </nav>

        {/* Footer */}
        <div className={`${collapsed ? 'p-2' : 'p-3'} border-t border-slate-200 dark:border-slate-700`}>
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex justify-center">
                  {user?.avatar ? (
                    <img src={user.avatar} alt={userName} className="w-10 h-10 rounded-lg object-cover cursor-pointer" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center text-white font-semibold text-sm cursor-pointer">
                      {userInitials}
                    </div>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p className="font-medium">{userName}</p>
                <p className="text-xs text-slate-400">{userRole}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <div className="flex items-center gap-3 p-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg transition-all hover:border-blue-300 dark:hover:border-blue-600 cursor-pointer">
              {user?.avatar ? (
                <img src={user.avatar} alt={userName} className="w-9 h-9 rounded-lg object-cover" />
              ) : (
                <div className="w-9 h-9 rounded-lg bg-blue-500 flex items-center justify-center text-white font-semibold text-sm">
                  {userInitials}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 dark:text-white truncate">{userName}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">{userRole}</div>
              </div>
              <button className="w-7 h-7 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-slate-600 transition-all">
                <MoreVertical className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;

